import { Component, Input, OnInit, Output, EventEmitter, AfterContentInit, inject, viewChild } from '@angular/core';

import { FormControl, FormGroup } from '@angular/forms';
import { Advertiser, Campaign, CampaignTargetUser, CampaignsAdvertiserAdminApiClient, CampaignsCampaignAdminApiClient, ProductReward, PunchCardReceiptToolType, PunchCardStrategy, ReceiptCheckOptions, ReceiptManualValidationRule, SecretCodeGuildlineVisual } from 'src/app/api/api-campaigns.service.generated';
import { ReceiptLocationCheckOptionsEditComponent } from '../../_/receipt-locations-check-options-edit/receipt-locations-check-options-edit.component';
import { PunchCardCampaignService } from '../punch-card-campaign-edit.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GUID } from 'src/app/common/utils/GuidUtils';
import { S3Upload } from 'src/app/common/utils/S3Upload';
import { PERMISSION } from 'src/app/const/permission';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PhotoComponent, ButtonComponent } from 'wecheer-common';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ReceiptCheckOptionsEditComponent } from '../../_/receipt-check-options-edit/receipt-check-options-edit.component';
import { FlexLayoutModule } from '@angular/flex-layout';
import { lastValueFrom } from 'rxjs';
import { UserPermissionsService } from 'src/app/common/services/user-permissions.service';

export class GuildlineVisualModel {
  img: string;
  uploadBannerKey: string;
}

@Component({
  selector: 'app-punch-card-strategy-receipt',
  templateUrl: './punch-card-strategy-receipt.component.html',
  styleUrls: ['./punch-card-strategy-receipt.component.scss'],
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatSlideToggleModule,
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    ReceiptLocationCheckOptionsEditComponent,
    ReceiptCheckOptionsEditComponent,
    PhotoComponent,
    ButtonComponent
]
})

export class PunchCardStrategyReceiptComponent implements OnInit, AfterContentInit {
  private advertiserApi = inject(CampaignsAdvertiserAdminApiClient);
  punchCardCampaignService = inject(PunchCardCampaignService);
  private campaignsApi = inject(CampaignsCampaignAdminApiClient);
  private snackBar = inject(MatSnackBar);
  private userPermissionsService = inject(UserPermissionsService);


  @Input() strategy: PunchCardStrategy;
  @Input() advertiserId: string;
  @Input() campaignId: string;
  @Input() originalCampaignId: string;
  @Input() isContactSync: boolean;

  @Output() onCaptureToolTypeChanged: EventEmitter<any> = new EventEmitter();
  readonly receiptLocationCheck = viewChild<ReceiptLocationCheckOptionsEditComponent>('receiptLocationCheck');

  advertiser: Advertiser;
  campaigns: Campaign[];
  rewardProductsOptions: ProductReward[] = [];
  receiptToolTypes: { name: string, value?: PunchCardReceiptToolType, index: number }[] = [];
  allowScanning: boolean = false;
  allowScanningList = [PunchCardReceiptToolType.FiscalQRSephaz, PunchCardReceiptToolType.OCR];
  allowStaffRoleList = [PunchCardReceiptToolType.UniqueQRCode, PunchCardReceiptToolType.OCR, PunchCardReceiptToolType.SecretCode, PunchCardReceiptToolType.HandshakeQR, PunchCardReceiptToolType.AdvertiserCampaign];
  isSecretCode: boolean = false;
  isGLOIntegration: boolean = false;
  guidelineImgs: GuildlineVisualModel[] = [];
  isOCR: boolean = false;
  isBrandAdmin = false;

  // Define the base list of allowed capture tool types
  allowedCaptureToolTypes = [
    PunchCardReceiptToolType.OCR,
    PunchCardReceiptToolType.AdvertiserCampaign,
    PunchCardReceiptToolType.External,
    PunchCardReceiptToolType.SecretCode,
    PunchCardReceiptToolType.UniqueQRCode
  ];

  additionalToolTypes: PunchCardReceiptToolType[] = [];
  selectedAdditionalToolType: PunchCardReceiptToolType = null;
  receiptToolTypeIds: PunchCardReceiptToolType[] = [];

  // Get allowed additional tool types based on the primary selection
  getAllowedAdditionalToolTypes(): PunchCardReceiptToolType[] {
    const primaryType = this.form.get('receiptToolTypeId').value;

    switch (primaryType) {
      case PunchCardReceiptToolType.OCR:
        return [PunchCardReceiptToolType.External, PunchCardReceiptToolType.AdvertiserCampaign];

      case PunchCardReceiptToolType.SecretCode:
        return [PunchCardReceiptToolType.External];

      case PunchCardReceiptToolType.UniqueQRCode:
        return [PunchCardReceiptToolType.External];

      case PunchCardReceiptToolType.AdvertiserCampaign:
        return [PunchCardReceiptToolType.External, PunchCardReceiptToolType.OCR];

      case PunchCardReceiptToolType.External:
        return [
          PunchCardReceiptToolType.AdvertiserCampaign,
          PunchCardReceiptToolType.OCR,
          PunchCardReceiptToolType.SecretCode,
          PunchCardReceiptToolType.UniqueQRCode
        ];

      default:
        return [];
    }
  }

  isLoading = false;

  form = new FormGroup({
    receiptToolTypeId: new FormControl(null),
    fingerprintRegex: new FormControl(''),
    receiptCheck: new FormControl(),
    timeConstraint: new FormControl(false),
    receiptLocationCheck: new FormControl(),
    receiptManualVerification: new FormControl(false),
    gloOrderSync: new FormControl(false),
    receiptToolTypeIds: new FormControl([]),
    manualValidation: new FormGroup({
      maxReceipts: new FormControl(5, { nonNullable: true }),
      windowDays: new FormControl(7, { nonNullable: true }),
      enabled: new FormControl(false, { nonNullable: true })
    })
  });

  ALLOWED_EXTENSIONS_BANNER = ['jpg', 'jpeg', 'png', 'gif'];

  ngAfterContentInit(): void {
    if (!this.advertiserId) return;

    this.getAdvertiser();
    setTimeout(() => {
      this.setFormValue(this.strategy);
      this.getReceiptToolTypes();
      if (!this.strategy) {
        this.handleNonStaffRole();
      }
    }, 500);
  }

  async ngOnChanges() {
    if (!this.isContactSync) {
      const frm = this.getFormValue();
      if (frm) {
        frm.gloOrderSync = false;
        this.setFormValue(frm);
      }
    }
  }

  async ngOnInit() {
    if (this.strategy && this.strategy.receiptToolTypeId) {
      this.onCaptureToolTypeChanged.emit(this.strategy.receiptToolTypeId);
    }
    this.userPermissionsService.hasPermission(PERMISSION.BRANDADMIN).subscribe(isBrandAdmin => this.isBrandAdmin = isBrandAdmin);
  }

  async getAdvertiser() {
    this.advertiser = await lastValueFrom(this.advertiserApi.getAdvertiserById(this.advertiserId));
  }

  private getReceiptToolTypes(): void {
    this.receiptToolTypes = [];
    for (const key in PunchCardReceiptToolType)
      if (key) {
        this.receiptToolTypes.push({ name: key, value: PunchCardReceiptToolType[key] as PunchCardReceiptToolType, index: Object.keys(PunchCardReceiptToolType).indexOf(key) + 1 });
      }

    if (this.isBrandAdmin) {
      this.receiptToolTypes = this.receiptToolTypes.filter(x => x.value === PunchCardReceiptToolType.UniqueQRCode);
    }
  }

  async setFormValue(model: PunchCardStrategy) {
    if (!model)
      return;

    this.allowScanning = this.allowScanningList.includes(model.receiptToolTypeId);
    this.isSecretCode = model.receiptToolTypeId === PunchCardReceiptToolType.SecretCode;
    this.isGLOIntegration = model.receiptToolTypeId === PunchCardReceiptToolType.GLOIntegration;
    this.isOCR = model.receiptToolTypeId === PunchCardReceiptToolType.OCR;

    // Initialize additional receipt tool types from the model
    if (model.receiptToolTypeIds && model.receiptToolTypeIds.length > 1) {
      // Extract additional tool types (all except the primary one)
      this.additionalToolTypes = model.receiptToolTypeIds.filter(type => type !== model.receiptToolTypeId);

      // Check if additionalToolTypes has specific receipt tool types and set flags accordingly
      if (!this.allowScanning) {
        this.allowScanning = this.additionalToolTypes.some(type => this.allowScanningList.includes(type));
      }

      if (!this.isSecretCode) {
        this.isSecretCode = this.additionalToolTypes.some(type => type === PunchCardReceiptToolType.SecretCode);
      }

      if (!this.isGLOIntegration) {
        this.isGLOIntegration = this.additionalToolTypes.some(type => type === PunchCardReceiptToolType.GLOIntegration);
      }

      if (!this.isOCR) {
        this.isOCR = this.additionalToolTypes.some(type => type === PunchCardReceiptToolType.OCR);
      }
    } else {
      this.additionalToolTypes = [];
    }

    // Save all tool types for form value retrieval
    this.receiptToolTypeIds = model.receiptToolTypeIds || (model.receiptToolTypeId ? [model.receiptToolTypeId] : []);

    setTimeout(() => {
      this.receiptLocationCheck()?.setFormValue(model.receiptLocationCheck);
    });

    this.form.patchValue({
      receiptManualVerification: model.receiptManualVerification || false,
      gloOrderSync: model.gloOrderSync || false,
      receiptToolTypeId: model.receiptToolTypeId,
      fingerprintRegex: model.fingerprintRegex,
      receiptCheck: model.receiptCheck ? new ReceiptCheckOptions({ ...model.receiptCheck }) : undefined,
      timeConstraint: model.timeConstraint || false,
      receiptToolTypeIds: this.receiptToolTypeIds || [],
      manualValidation: {
        maxReceipts: model.receiptManualValidationRule?.maxReceipts ?? 5,
        windowDays: model.receiptManualValidationRule?.windowDays ?? 7,
        enabled: model.receiptManualValidationRule?.enabled ?? false
      }
    });
    if (model.secretCodeGuildlineVisuals) {
      this.guidelineImgs = [];
      model.secretCodeGuildlineVisuals.forEach(gv => {
        let visual = new GuildlineVisualModel();
        visual.img = gv.guildlineVisualUrl;
        this.guidelineImgs.push(visual);
      });
    }
  }

  getFormValue() {
    const f = this.form.value;

    if (!this.isValid()) {
      return null;
    }

    let model: PunchCardStrategy = new PunchCardStrategy({});
    // scan
    model.receiptManualVerification = f.receiptManualVerification;
    model.gloOrderSync = f.gloOrderSync;
    model.allowScanning = f.receiptToolTypeId == PunchCardReceiptToolType.OCR;
    model.fingerprintRegex = f.fingerprintRegex;
    model.receiptCheck = f.receiptCheck;
    const receiptLocationCheck = this.receiptLocationCheck();
    model.receiptLocationCheck = receiptLocationCheck && receiptLocationCheck.getFormValue();
    model.receiptToolTypeId = f.receiptToolTypeId;

    // Set the receiptToolTypeIds array with all selected types
    if (this.receiptToolTypeIds.length > 0) {
      model.receiptToolTypeIds = [...this.receiptToolTypeIds];
    } else if (f.receiptToolTypeId) {
      model.receiptToolTypeIds = [f.receiptToolTypeId];
    }

    model.secretCodeGuildlineVisuals = this.guidelineImgs.map(x => new SecretCodeGuildlineVisual({
      guildlineVisualUrl: x.img,
      displayOrder: this.guidelineImgs.indexOf(x)
    }));
    model.timeConstraint = f.timeConstraint;
    // ManualValidation
    model.receiptManualValidationRule = new ReceiptManualValidationRule({
      maxReceipts: f.manualValidation.maxReceipts,
      windowDays: f.manualValidation.windowDays,
      enabled: f.manualValidation.enabled
    });
    return model;
  }

  onReceiptToolTypeChange(event: any) {
    this.allowScanning = this.allowScanningList.includes(this.form.get('receiptToolTypeId').value);
    this.isSecretCode = this.form.get('receiptToolTypeId').value === PunchCardReceiptToolType.SecretCode;
    this.isGLOIntegration = this.form.get('receiptToolTypeId').value === PunchCardReceiptToolType.GLOIntegration;
    this.isOCR = this.form.get('receiptToolTypeId').value === PunchCardReceiptToolType.OCR;
    this.onCaptureToolTypeChanged.emit(event.value);

    // Clear additional tool types when primary type changes
    this.additionalToolTypes = [];
    this.updateReceiptToolTypeIds();
  }

  onAdditionalToolTypeChange(index: number) {
    // Get the selected tool type at this index
    const selectedType = this.additionalToolTypes[index];

    // Update flags based on the selected tool type
    if (selectedType) {
      // Check if it enables scanning
      if (!this.allowScanning) {
        this.allowScanning = this.allowScanningList.includes(selectedType);
      }

      // Check for specific tool types
      if (!this.isSecretCode && selectedType === PunchCardReceiptToolType.SecretCode) {
        this.isSecretCode = true;
      }

      if (!this.isGLOIntegration && selectedType === PunchCardReceiptToolType.GLOIntegration) {
        this.isGLOIntegration = true;
      }

      if (!this.isOCR && selectedType === PunchCardReceiptToolType.OCR) {
        this.isOCR = true;
      }
    }

    // When a selection is made, update the receiptToolTypeIds array
    this.updateReceiptToolTypeIds();
  }

  addEmptyToolType() {
    // Add an empty slot for a new tool type selection
    this.additionalToolTypes.push(null);
  }

  removeToolType(index: number) {
    // Remove the selected tool type from the additionalToolTypes array
    this.additionalToolTypes.splice(index, 1);

    // Update the receiptToolTypeIds to reflect the removal
    this.updateReceiptToolTypeIds();

    // Update the form value to reflect the changes
    let frm = this.getFormValue();
    frm.receiptToolTypeId = this.form.get('receiptToolTypeId').value;
    frm.receiptToolTypeIds = this.receiptToolTypeIds;

    // Set the updated form value
    this.setFormValue(frm);
  }

  getToolTypeName(toolTypeId: PunchCardReceiptToolType): string {
    const toolType = this.receiptToolTypes.find(t => t.value === toolTypeId);
    return toolType ? toolType.name : toolTypeId;
  }

  getAvailableToolTypes(currentIndex?: number) {
    const selectedValues = this.additionalToolTypes.filter((type, i) =>
      type !== null && (currentIndex === undefined || i !== currentIndex));

    const primaryType = this.form.get('receiptToolTypeId').value;

    // Special case for External as primary: options depend on what's already selected
    if (primaryType === PunchCardReceiptToolType.External) {
      // Check what's already selected in additional dropdowns
      const hasSecretCode = selectedValues.includes(PunchCardReceiptToolType.SecretCode);
      const hasUniqueQRCode = selectedValues.includes(PunchCardReceiptToolType.UniqueQRCode);
      const hasAdvertiserCampaign = selectedValues.includes(PunchCardReceiptToolType.AdvertiserCampaign);
      const hasOCR = selectedValues.includes(PunchCardReceiptToolType.OCR);

      // Case 1: SecretCode/UniqueQRCode selection logic
      if (hasSecretCode && !hasUniqueQRCode) {
        // If SecretCode is selected, only show UniqueQRCode
        return this.receiptToolTypes.filter(type =>
          type.value === PunchCardReceiptToolType.UniqueQRCode &&
          !selectedValues.includes(type.value));
      } else if (hasUniqueQRCode && !hasSecretCode) {
        // If UniqueQRCode is selected, only show SecretCode
        return this.receiptToolTypes.filter(type =>
          type.value === PunchCardReceiptToolType.SecretCode &&
          !selectedValues.includes(type.value));
      }

      // Case 2: AdvertiserCampaign/OCR selection logic
      if (hasAdvertiserCampaign && !hasOCR) {
        // If AdvertiserCampaign is selected, only show OCR
        return this.receiptToolTypes.filter(type =>
          type.value === PunchCardReceiptToolType.OCR &&
          !selectedValues.includes(type.value));
      } else if (hasOCR && !hasAdvertiserCampaign) {
        // If OCR is selected, only show AdvertiserCampaign
        return this.receiptToolTypes.filter(type =>
          type.value === PunchCardReceiptToolType.AdvertiserCampaign &&
          !selectedValues.includes(type.value));
      }
    }

    // Get allowed additional types based on selected primary type
    const allowedAdditionalTypes = this.getAllowedAdditionalToolTypes();

    // Filter tool types to only include those in the allowed list for this primary type
    return this.receiptToolTypes.filter(type =>
      allowedAdditionalTypes.includes(type.value) &&
      type.value !== primaryType &&
      !selectedValues.includes(type.value));
  }

  updateReceiptToolTypeIds() {
    // Collect all selected tool types (primary + additionals)
    const primaryType = this.form.get('receiptToolTypeId').value;
    if (primaryType) {
      // Start with the primary type
      const allTypes = [primaryType];

      // Add all valid additional types
      this.additionalToolTypes.forEach(type => {
        if (type !== null && type !== primaryType && !allTypes.includes(type)) {
          allTypes.push(type);
        }
      });

      // Update the model
      this.receiptToolTypeIds = allTypes;
    }
  }

  onAddAdditionalToolType() {
    if (this.selectedAdditionalToolType && !this.receiptToolTypeIds.includes(this.selectedAdditionalToolType)) {
      this.receiptToolTypeIds.push(this.selectedAdditionalToolType);
      // Reset the select after adding
      this.selectedAdditionalToolType = null;
    }
  }

  // Method to control when to show the add additional button
  showAddAdditionalButton(): boolean {
    const primaryType = this.form.get('receiptToolTypeId').value;

    // Special case for External primary type
    if (primaryType === PunchCardReceiptToolType.External) {
      // Check what's already selected in additional dropdowns
      const selectedTypes = this.additionalToolTypes.filter(type => type !== null);

      // If we have OCR and AdvertiserCampaign or SecretCode and UniqueQRCode, don't show add button
      const hasOCR = selectedTypes.includes(PunchCardReceiptToolType.OCR);
      const hasAdvertiserCampaign = selectedTypes.includes(PunchCardReceiptToolType.AdvertiserCampaign);
      const hasSecretCode = selectedTypes.includes(PunchCardReceiptToolType.SecretCode);
      const hasUniqueQRCode = selectedTypes.includes(PunchCardReceiptToolType.UniqueQRCode);

      // If we already have both OCR and AdvertiserCampaign, don't show the add button
      if (hasOCR && hasAdvertiserCampaign) {
        return false;
      }

      // If we already have both SecretCode and UniqueQRCode, don't show the add button
      if (hasSecretCode && hasUniqueQRCode) {
        return false;
      }

      if ((hasOCR || hasAdvertiserCampaign) && (hasSecretCode || hasUniqueQRCode)) {
        return false;
      }
    }

    // General case for all primary types
    return primaryType !== null &&
      primaryType !== PunchCardReceiptToolType.None &&
      this.allowedCaptureToolTypes.includes(primaryType) &&
      this.additionalToolTypes.length < this.receiptToolTypes.length - 1 &&
      this.getAvailableToolTypes().length > 0; // Only show if there are available options
  }

  forceValidatorsToDisplayErrors() {
    this.form.markAllAsTouched();
  }

  isValid() {
    let isValid = this.form.valid;

    if (!isValid)
      this.forceValidatorsToDisplayErrors();

    return isValid;
  }

  async onSubmit() {
  }

  compareFn: ((a: any, b: any) => boolean) | null = (a: any, b: any) => a && b && a.id === b.id;

  async onBannerImageSelected(event, item) {
    try {
      const file = event.target.files[0];
      if (!file || !this.isValidBanner(file)) {
        this.snackBar.open('Invalid image file', 'Dismiss', { duration: 2000 });
        return;
      }

      item.uploadBannerKey = GUID.newGuid().replace(/-/g, '') + '.' + file.name.split('.').pop(); // preserve extension on s3 key
      const uploadUrl = await lastValueFrom(this.campaignsApi.getUploadUrl(item.uploadBannerKey));
      const uploadSuccess = await lastValueFrom(S3Upload.upload(uploadUrl, file));
      if (uploadSuccess) {
        const parts = uploadUrl.split('?');
        const photoBannerUrl = parts[0];
        item.img = photoBannerUrl.replace('s3.ap-southeast-1.amazonaws.com/', ''); // trim s3 part to get photo using cloudfront
      }
    } catch (err) {
      console.error('upload failed', err);
    }
  }

  isValidBanner(file: File): boolean {
    return file.name && this.ALLOWED_EXTENSIONS_BANNER.filter(e => file.name.trim().toLowerCase().endsWith(e)).length > 0;
  }

  addNewVisual() {
    if (this.guidelineImgs.length === 3) return;

    const visual = new GuildlineVisualModel();
    this.guidelineImgs.push(visual);
  }

  removeVisual(event: any, index: number) {
    event.stopPropagation();
    this.guidelineImgs.splice(index, 1);
  }

  onRoleChange() {
    if (this.punchCardCampaignService.targetRole === CampaignTargetUser.Staff) {
      this.handleStaffRole();
    } else {
      this.handleNonStaffRole();
    }
  }

  private handleStaffRole() {
    this.receiptToolTypes = [
      {
        name: PunchCardReceiptToolType.OCR,
        value: PunchCardReceiptToolType.OCR,
        index: Object.keys(PunchCardReceiptToolType).indexOf(PunchCardReceiptToolType.OCR) + 1,
      },
      {
        name: PunchCardReceiptToolType.UniqueQRCode,
        value: PunchCardReceiptToolType.UniqueQRCode,
        index: Object.keys(PunchCardReceiptToolType).indexOf(PunchCardReceiptToolType.UniqueQRCode) + 1,
      },
      {
        name: PunchCardReceiptToolType.SecretCode,
        value: PunchCardReceiptToolType.SecretCode,
        index: Object.keys(PunchCardReceiptToolType).indexOf(PunchCardReceiptToolType.SecretCode) + 1,
      },
      {
        name: PunchCardReceiptToolType.HandshakeQR,
        value: PunchCardReceiptToolType.HandshakeQR,
        index: Object.keys(PunchCardReceiptToolType).indexOf(PunchCardReceiptToolType.HandshakeQR) + 1,
      },
      {
        name: PunchCardReceiptToolType.AdvertiserCampaign,
        value: PunchCardReceiptToolType.AdvertiserCampaign,
        index: Object.keys(PunchCardReceiptToolType).indexOf(PunchCardReceiptToolType.AdvertiserCampaign) + 1,
      }
    ];

    let receiptToolTypeId = this.form.get('receiptToolTypeId').value;
    const isValidToolSelected = this.allowStaffRoleList.includes(receiptToolTypeId);
    this.updateReceiptToolType(isValidToolSelected ? receiptToolTypeId : null);
  }

  private handleNonStaffRole() {
    this.getReceiptToolTypes();

    // The HandshakeQR tool type is only for staff
    this.receiptToolTypes = this.receiptToolTypes.filter(type =>
      type.value !== PunchCardReceiptToolType.HandshakeQR
    );
  }

  private updateReceiptToolType(receiptToolTypeId: PunchCardReceiptToolType | null) {
    this.form.get('receiptToolTypeId').setValue(receiptToolTypeId);
    this.onCaptureToolTypeChanged.emit(receiptToolTypeId);
  }
}
